import React, { useContext, useEffect, useState } from "react"
import AdHocCommands, { GpsConfigEntry } from "@/api/adHocCommands"
import XMPPError from "@xmpp/error"
import { XmppContext } from "@/stream/xmppClient"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Switch } from "@/components/ui/switch"
import Loader from "@/components/Loader"
import { useTranslation } from "react-i18next"

interface EditGpsProviderModalProps {
  config: GpsConfigEntry | null
  onClose: () => void
  onSuccess: () => void
}

const isPasswordLike = (key: string) =>
  /password|secret|token|key|apikey|api_key|pw/i.test(key)

const sortCredentialKeys = (fields: { var: string; label: string }[]) =>
  [...fields].sort(
    (a, b) => Number(isPasswordLike(a.var)) - Number(isPasswordLike(b.var)),
  )

const EditGpsProviderModal: React.FC<EditGpsProviderModalProps> = ({
  config,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation()
  const { client } = useContext(XmppContext)

  // Step-1 state
  const [sessionid, setSessionid] = useState<string | null>(null)
  const [fetchingForm, setFetchingForm] = useState(false)

  // Form state — populated from step-1 response
  const [name, setName] = useState("")
  const [enabled, setEnabled] = useState(true)
  const [credentialFields, setCredentialFields] = useState<
    { var: string; label: string }[]
  >([])
  const [credentials, setCredentials] = useState<Record<string, string>>({})

  // Step-2 state
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Fetch the pre-filled form from the server whenever config changes
  useEffect(() => {
    if (!config) return

    setFetchingForm(true)
    setSessionid(null)
    setCredentials({})
    setError(null)

    AdHocCommands.fetchGpsProviderEditForm(client, config.id)
      .then(
        ({
          sessionid: sid,
          enabled: currentEnabled,
          name: currentName,
          credentialFields,
        }) => {
          setSessionid(sid)
          setEnabled(currentEnabled)
          setName(currentName)
          setCredentialFields(credentialFields)
        },
      )
      .catch((err) => {
        // "" means no server text: the fallback is translated where shown.
        const msg = err instanceof XMPPError && err.text ? err.text : ""
        setError(msg)
      })
      .finally(() => setFetchingForm(false))
  }, [config, client])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!config || !sessionid) return

    // Credentials: only send keys that were filled in; backend allows blank =
    // keep existing. But if *any* are filled, *all* must be filled.
    const filledFields = credentialFields.filter((f) =>
      credentials[f.var]?.trim(),
    )
    if (
      filledFields.length > 0 &&
      filledFields.length < credentialFields.length
    ) {
      const missing = credentialFields
        .filter((f) => !credentials[f.var]?.trim())
        .map((f) => f.label)
      setError(
        t("fleet.gpsProviderForm.missingOnUpdate", {
          fields: missing.join(", "),
        }),
      )
      return
    }

    setSubmitting(true)
    setError(null)
    try {
      await AdHocCommands.submitGpsProviderUpdate(
        client,
        sessionid,
        name,
        enabled,
        credentials,
      )
      onSuccess()
      handleClose()
    } catch (err) {
      const msg =
        err instanceof XMPPError && err.text
          ? err.text
          : t("fleet.gpsProviderForm.updateFailed")
      setError(msg)
    } finally {
      setSubmitting(false)
    }
  }

  const handleClose = () => {
    setSessionid(null)
    setName("")
    setCredentialFields([])
    setCredentials({})
    setError(null)
    onClose()
  }

  return (
    <Dialog
      open={config !== null}
      onOpenChange={(open) => !open && handleClose()}
    >
      <DialogContent className="sm:max-w-md">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>
              {t("fleet.gpsProviderForm.editTitle")}
              {config && (
                <span className="ml-2 text-sm font-normal text-muted-foreground">
                  {config.provider}
                </span>
              )}
            </DialogTitle>
          </DialogHeader>

          {fetchingForm ? (
            <div className="flex h-32 items-center justify-center">
              <Loader />
            </div>
          ) : (
            <div className="mt-4 space-y-5">
              {/* Name — pre-filled from server */}
              <div className="space-y-1">
                <Label htmlFor="gps-edit-name">
                  {t("fleet.gpsProviderForm.name")}
                </Label>
                <Input
                  id="gps-edit-name"
                  type="text"
                  autoComplete="off"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  disabled={!sessionid}
                />
              </div>

              {/* Enabled toggle — pre-filled from server */}
              <div className="flex items-center justify-between">
                <Label htmlFor="gps-enabled-toggle">
                  {t("fleet.gpsProviderForm.enabled")}
                </Label>
                <Switch
                  id="gps-enabled-toggle"
                  checked={enabled}
                  onCheckedChange={setEnabled}
                  disabled={!sessionid}
                />
              </div>

              {/* Credential fields — blank by default, leave blank to keep existing */}
              {credentialFields.length > 0 && (
                <div className="space-y-3">
                  <p className="text-xs text-muted-foreground">
                    {t("fleet.gpsProviderForm.credentialsHint")}
                  </p>
                  {sortCredentialKeys(credentialFields).map((field) => (
                    <div key={field.var} className="space-y-1">
                      <Label htmlFor={`cred-edit-${field.var}`}>
                        {field.label}
                      </Label>
                      <Input
                        id={`cred-edit-${field.var}`}
                        type={isPasswordLike(field.var) ? "password" : "text"}
                        autoComplete="off"
                        value={credentials[field.var] ?? ""}
                        onChange={(e) =>
                          setCredentials((prev) => ({
                            ...prev,
                            [field.var]: e.target.value,
                          }))
                        }
                        placeholder={t("fleet.gpsProviderForm.keepExisting")}
                        disabled={!sessionid}
                      />
                    </div>
                  ))}
                </div>
              )}

              {error !== null && (
                <p className="text-sm text-red-600 dark:text-red-400">
                  {error || t("fleet.gpsProviderForm.loadFailed")}
                </p>
              )}
            </div>
          )}

          <DialogFooter className="mt-6">
            <Button
              type="button"
              variant="ghost"
              onClick={handleClose}
              disabled={submitting}
            >
              {t("common.cancel")}
            </Button>
            <Button
              type="submit"
              disabled={submitting || fetchingForm || !sessionid}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {submitting
                ? t("fleet.gpsProviderForm.saving")
                : t("common.save")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export default EditGpsProviderModal
